import { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Share2, Copy, Smartphone, BookOpen, Download, ExternalLink } from 'lucide-react';

// WhatsApp brand glyph (kept minimal to avoid extra deps)
function WhatsAppIcon({ size = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .14 5.34.14 11.93a11.86 11.86 0 0 0 1.6 5.95L0 24l6.28-1.65a11.94 11.94 0 0 0 5.78 1.47h.01c6.55 0 11.92-5.34 11.92-11.93 0-3.18-1.24-6.18-3.47-8.41ZM12.07 21.8h-.01a9.86 9.86 0 0 1-5.02-1.37l-.36-.21-3.73.98 1-3.64-.24-.37a9.84 9.84 0 0 1-1.51-5.27c0-5.46 4.45-9.9 9.92-9.9 2.65 0 5.14 1.03 7.01 2.9a9.85 9.85 0 0 1 2.9 7.01c-.01 5.46-4.46 9.87-9.96 9.87Zm5.43-7.4c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.05 1.02-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.13 3.25 5.16 4.56.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.76-.72 2.01-1.42.25-.7.25-1.3.18-1.42-.07-.12-.27-.2-.57-.35Z" />
    </svg>
  );
}

/**
 * ShareCatalogMenu — dropdown with all share options for a catalog.
 *
 * Props:
 *   - catalog: { id, title, share_token }
 *   - onDownloadPdf: () => void  (already-bound PDF download trigger)
 *   - pdfBusy: boolean
 */
export default function ShareCatalogMenu({ catalog, onDownloadPdf, pdfBusy }) {
  const [waOpen, setWaOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const mobileLink = `${origin}/shared-mobile/${catalog.share_token}`;
  const flipbookLink = `${origin}/flipbook/${catalog.share_token}`;
  const desktopLink = `${origin}/shared/${catalog.share_token}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      // Fallback for older mobile browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success(`${label} copied`); }
      catch { toast.error('Copy failed — please copy manually'); }
      ta.remove();
    }
  };

  const buildWaMessage = () => {
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    const signature = trimmedName || trimmedPhone
      ? `Warm regards,\n${trimmedName || 'AARYA24kt'}${trimmedPhone ? `\n${trimmedPhone}` : ''}`
      : 'Warm regards,\nAARYA24kt';
    return [
      '\u2728 AARYA24kt Exclusive Catalog \u2728',
      '',
      'We are pleased to share our latest curated collection:',
      `*${catalog.title}*`,
      '',
      'Explore premium 24KT gold foil artworks designed for gifting, decor, and spiritual elegance.',
      '',
      '\uD83D\uDD17 View Catalog:',
      mobileLink,
      '',
      'For enquiries or bulk orders, feel free to connect.',
      '',
      signature,
    ].join('\n');
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(buildWaMessage())}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setWaOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`share-menu-trigger-${catalog.id}`}>
            <Share2 size={12} className="mr-1" />Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 bg-white border-[#E5E0D8]" data-testid={`share-menu-${catalog.id}`}>
          <DropdownMenuLabel className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C]">Share Catalog</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setWaOpen(true)} className="text-sm cursor-pointer" data-testid={`share-whatsapp-${catalog.id}`}>
            <span className="mr-2 text-[#25D366]"><WhatsAppIcon size={14} /></span>
            <div className="flex flex-col">
              <span>Share via WhatsApp</span>
              <span className="text-[10px] text-[#888]">Pre-filled luxury message</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => copy(mobileLink, 'Mobile link')} className="text-sm cursor-pointer" data-testid={`copy-mobile-${catalog.id}`}>
            <Copy size={14} className="mr-2 text-[#C5A059]" />
            <div className="flex flex-col">
              <span>Copy Mobile Link <span className="text-[9px] text-[#C5A059] ml-1">recommended</span></span>
              <span className="text-[10px] text-[#888] truncate max-w-[170px]">/shared-mobile/{catalog.share_token?.slice(0, 8)}…</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => copy(flipbookLink, 'Flipbook link')} className="text-sm cursor-pointer" data-testid={`copy-flipbook-${catalog.id}`}>
            <Copy size={14} className="mr-2 text-[#5C5C5C]" />Copy Flipbook Link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => copy(desktopLink, 'Desktop link')} className="text-sm cursor-pointer" data-testid={`copy-desktop-${catalog.id}`}>
            <Copy size={14} className="mr-2 text-[#5C5C5C]" />Copy Desktop Link
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => window.open(mobileLink, '_blank', 'noopener,noreferrer')} className="text-sm cursor-pointer" data-testid={`open-mobile-${catalog.id}`}>
            <Smartphone size={14} className="mr-2 text-[#C5A059]" />Open Mobile View
            <ExternalLink size={10} className="ml-auto text-[#888]" />
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(flipbookLink, '_blank', 'noopener,noreferrer')} className="text-sm cursor-pointer" data-testid={`open-flipbook-${catalog.id}`}>
            <BookOpen size={14} className="mr-2 text-[#5C5C5C]" />Open Flipbook
            <ExternalLink size={10} className="ml-auto text-[#888]" />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onDownloadPdf?.()} disabled={pdfBusy} className="text-sm cursor-pointer" data-testid={`download-pdf-menu-${catalog.id}`}>
            <Download size={14} className="mr-2 text-[#5C5C5C]" />{pdfBusy ? 'Preparing PDF…' : 'Download PDF'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* WhatsApp share dialog */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8] max-w-md" data-testid={`whatsapp-dialog-${catalog.id}`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2">
              <span className="text-[#25D366]"><WhatsAppIcon size={18} /></span>
              Share via WhatsApp
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              Optionally personalise the signature. The mobile-friendly catalog link will be attached.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="wa-name" className="text-xs uppercase tracking-wider text-[#5C5C5C]">Your Name <span className="text-[10px] text-[#888] normal-case tracking-normal">(optional)</span></Label>
              <Input id="wa-name" placeholder="e.g. Rohan Mehta" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm border-[#E5E0D8] mt-1" data-testid={`whatsapp-name-input-${catalog.id}`} />
            </div>
            <div>
              <Label htmlFor="wa-phone" className="text-xs uppercase tracking-wider text-[#5C5C5C]">Phone Number <span className="text-[10px] text-[#888] normal-case tracking-normal">(optional)</span></Label>
              <Input id="wa-phone" placeholder="+91 98xxx xxxxx" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-sm border-[#E5E0D8] mt-1" data-testid={`whatsapp-phone-input-${catalog.id}`} />
            </div>
            <div className="bg-[#FBF7EE] border border-[#E5E0D8] rounded-sm p-3 max-h-44 overflow-y-auto">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-2">Message preview</p>
              <pre className="text-xs whitespace-pre-wrap font-sans text-[#1A1A1A] leading-relaxed" data-testid={`whatsapp-preview-${catalog.id}`}>{buildWaMessage()}</pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaOpen(false)} className="rounded-sm border-[#E5E0D8]" data-testid={`whatsapp-cancel-${catalog.id}`}>Cancel</Button>
            <Button onClick={openWhatsApp} className="bg-[#25D366] hover:bg-[#1FAE54] text-white rounded-sm" data-testid={`whatsapp-send-${catalog.id}`}>
              <span className="mr-2"><WhatsAppIcon size={14} /></span>Open WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
